import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import { toast } from 'react-toastify';
import { Grid2, Typography, useMediaQuery, Divider, Button, Card, CardContent } from '@mui/material';

import Layout from 'components/Layout';
import EditIcon from 'assets/icons/edit-white.svg';

import { useTranslation } from 'core/contexts/localization';
import { useExperience } from 'core/contexts/experience';

import { useStyles } from './styles';

import CalendarPage from './components/Calendar';

import { Title, TextInput, CheckboxComponent, CircularIndeterminate, SelectComponent, UploadPDFModal, PDFViewer, BasicTable, CurrencyInput, AlertDialog } from '@utriper/core-ui';
import { ExperienceStatus, PayNow } from '@utriper/core';

import { usePrice } from 'core/contexts/price';
import { useInsurance } from 'core/contexts/insurance';
import { useTerms } from 'core/contexts/terms';
import { TermsService } from 'core/api/services/terms';
import { useCosts } from 'core/contexts/cost';
import CostForm from 'components/CostForm';
import { Method } from 'core/api/fetch';

const ExperiencePage = () => {
    const classes = useStyles();
    const history = useHistory();
    const isSmall = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const { t: translate, i18n } = useTranslation();

    const { insurances, getListInsures, getInsurancesProducts, insuranceProducts } = useInsurance();

    const { experiencesTerms, getExperiencesTerms } = useTerms();
    const { experienceDetails, getExperienceDetails, updateExperienceDetail } = useExperience();
    const statusOptions = useMemo(
        () =>
            ExperienceStatus?.getIds().map((id) => {
                const translated = ExperienceStatus.getTranslatedName(ExperienceStatus.status, id, i18n.language);
                return { name: translated?.[0] ?? '', id };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ExperienceStatus, i18n.language]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const payNowOptions = useMemo(() =>
        PayNow?.getIds().map((id) => {
            const translated = PayNow.getTranslatedName(PayNow.types, id, i18n.language);
            return { name: translated?.[0] ?? '', id };
        })
    );
    const handleListItemClick = (expTerm) => {
        const selected = experiencesTerms.find((expTerms) => expTerms.id === expTerm.id);
        setSelectedPDF(selected);
        setPageNumber(1);
    };

    const {
        loading,
        experience,
        getExperience,
        setExperience,
        updateExperience,

        // Costs
        getCosts,
        postCost,
        patchCost,
        deleteCost,
        costs,
        loadingSubmit,
    } = useExperience();

    const { costColumns, prettifyCosts } = useCosts();

    const { priceGroup, getPriceGroup } = usePrice();

    const [experienceChanged, setExperienceChanged] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [experienceInfo, setExperienceInfo] = useState({});
    const [experienceData, setExperienceData] = useState({});
    const [requireId, setRequireId] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [payByCash, setPayByCash] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [payByTerminal, setPayByTerminal] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [payByLink, setPayByLink] = useState(false);
    const [openUploadPDF, setOpenUploadPDF] = useState(false);
    const [selectedPDF, setSelectedPDF] = useState(null);
    const [openPDFViewer, setOpenPDFViewer] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [openCostForm, setOpenCostForm] = useState(false);
    const [openExpDetail, setOpenExpDetail] = useState(false);
    const [openDeleteCostForm, setOpenDeleteCostForm] = useState(false);
    const [costToEdit, setCostToEdit] = useState(null);
    const [openExpDetailForm, setOpenExpDetailForm] = useState(false);
    const [expDetailToEdit, setExpDetailToEdit] = useState(null);
    const [expDetailFormChanged, setExpDetailFormChanged] = useState(false);

    const [costId, setCostId] = useState(null);

    const locationParam = document.location.href.split('/');
    const experienceIndex = parseInt(locationParam[locationParam.length - 1]);

    const onSubmit = async (values) => {
        try {
            if (experienceIndex === 0) {
                history.push(`/experience`);
            } else {
                const valuesClone = { ...values };
                valuesClone.requireId = requireId;
                valuesClone.payByCash = payByCash;
                valuesClone.payByTerminal = payByTerminal;
                valuesClone.payByLink = payByLink;

                if (valuesClone.insuranceProviderId === 0) {
                    valuesClone.insuranceProductId = null;
                } else if (!valuesClone.insuranceProductId) {
                    valuesClone.insuranceProviderId = null;
                }

                if (!valuesClone.price) valuesClone.price = null;

                if (experienceChanged) {
                    await updateExperience(valuesClone);
                }
                setCanEdit(false);
                getExperience(experienceIndex);
            }
        } catch (err) {
            toast.error(err, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };
    useEffect(() => {
        setExperience({});
        getExperience(experienceIndex);
        // }
        getListInsures();
        getExperiencesTerms(experienceIndex);
        getExperienceDetails(experienceIndex);
        getCosts(experienceIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const insuranceProvider = [{ id: 0, name: 'N/A' }, ...insurances];
    const insuranceProduct = [...insuranceProducts];

    useEffect(() => {
        setExperienceData(experience);
        setRequireId(experience.requireId || false);
        setExperienceInfo(experience);
        if (experience.insuranceProviderId) {
            getInsurancesProducts(experience.insuranceProviderId);
        }
        if (experience.id) {
            getPriceGroup(experience.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experience]);

    useEffect(() => {
        if (canEdit && experienceInfo.insuranceProviderId) {
            getInsurancesProducts(experienceInfo.insuranceProviderId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEdit, experienceInfo.insuranceProviderId]);

    const handleOpenUploadPDF = () => setOpenUploadPDF(true);
    const handleCloseUploadPDF = () => setOpenUploadPDF(false);

    const onSubmitPDF = async (values) => {
        try {
            const result = await TermsService.savePDF(experienceIndex, values);

            if (result) {
                toast.success(translate('experience_terms_page.success_new_policies'), {
                    position: 'top-right',
                    theme: 'colored',
                });
            } else {
                throw new Error(translate('experience_terms_page.error_new_policies'));
            }

            setOpenUploadPDF(false);
            getExperiencesTerms(experienceIndex);
        } catch (error) {
            toast.error(error, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    const expTermsColumns = [
        {
            align: 'left',
            val: 'id',
            label: 'Id',
        },
        {
            val: 'TermsName',
            label: 'Terms Name',
        },
        {
            align: 'left',
            val: 'preview',
            label: 'Preview',
        },
        {
            val: 'version',
            label: 'Version',
        },
        {
            val: 'isActive',
            label: 'Is Active',
        },
    ];

    const expDetailColumns = [
        {
            align: 'left',
            val: 'id',
            label: 'Id',
        },
        {
            val: 'name',
            label: 'Name',
        },
        {
            val: 'status',
            label: 'Status',
        },
        {
            align: 'left',
            val: 'viatorReference',
            label: 'Viator Reference',
        },
        {
            val: 'gygActivityReference',
            label: 'GYG Activity Reference',
        },
        {
            val: 'octoReference',
            label: 'Octo Reference',
        },
        {
            val: 'actions',
            label: ' ',
            align: 'center',
        },
    ];
    const onSubmitExpDetail = async (values) => {
        try {
            if (!expDetailToEdit || !expDetailToEdit.id) {
                toast.error('No experience detail selected to edit');
                return;
            }
            const valuesClone = { expId: experienceIndex, ...values };
            await updateExperienceDetail(valuesClone);

            toast.success('Experience detail updated successfully', {
                position: 'top-right',
                theme: 'colored',
            });

            // Reset form and close modal
            setOpenExpDetailForm(false);
            setExpDetailToEdit(null);
            setExpDetailFormChanged(false);

            // Refresh experience details
            getExperienceDetails(experienceIndex);
        } catch (error) {
            toast.error(error.message || 'Error updating experience detail', {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    const handleSetActiveExperienceTerm = async (selectedId) => {
        try {
            await updateExperience({ ...experience, currentTermsId: selectedId });
            getExperience(experienceIndex);
            getExperiencesTerms(experienceIndex);
        } catch (error) {
            toast.error('Error updating current terms');
        }
    };

    const prettifyExperienceTerms = (experiencesTerms = []) => {
        return experiencesTerms.map((experienceTerm) => ({
            ...experienceTerm,
            preview: (
                <Button
                    onClick={() => {
                        handleListItemClick(experienceTerm);
                        setOpenPDFViewer(true);
                    }}
                    variant="text"
                    style={{
                        color: '#1976d2',
                        textTransform: 'none',
                        padding: 0,
                        minWidth: 'auto',
                        fontSize: '14px',
                        textDecoration: 'underline',
                    }}>
                    Ver Preview
                </Button>
            ),
            isActive: (
                <div
                    onClick={() => handleSetActiveExperienceTerm(experienceTerm.id)}
                    style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: experienceTerm.isActive ? '#4caf50' : '#ccc',
                        cursor: 'pointer',
                        margin: 'auto',
                    }}
                />
            ),
        }));
    };

    const prettifyExperienceDetails = (experienceDetails = []) => {
        return experienceDetails.map((expDetail) => ({
            ...expDetail,
            status: ExperienceStatus.getTranslatedName(ExperienceStatus.status, expDetail.status, i18n.language)?.[0] || '',
            actions: (
                <Button onClick={() => handleClickEditExpDetail(expDetail)} variant="outlined" size="contained">
                    {translate('btn_edit')}
                </Button>
            ),
        }));
    };
    const renderExpDetailForm = () => {
        return (
            <Form
                onSubmit={onSubmitExpDetail}
                initialValues={expDetailToEdit}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <Grid2 container spacing={3}>
                            <Grid2 size={12} style={{ marginTop: '10px' }}>
                                <Field name="gygActivityReference" component={TextInput} label="GYG Activity Reference" hintText="Ingrese la referencia de actividad GYG" fullWidth onFieldChange={() => setExpDetailFormChanged(true)} />
                            </Grid2>

                            <Grid2 size={12}>
                                <Field name="viatorReference" component={TextInput} label="Viator Reference" hintText="Ingrese la referencia de Viator" fullWidth onFieldChange={() => setExpDetailFormChanged(true)} />
                            </Grid2>
                            <Grid2 size={12}>
                                <Field name="octoReference" component={TextInput} label="Octo Reference" hintText="Ingrese la referencia de Octo" fullWidth onFieldChange={() => setExpDetailFormChanged(true)} />
                            </Grid2>
                        </Grid2>

                        <Grid2 container justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
                            <Grid2>
                                <Button type="button" onClick={handleCloseExpDetailForm} variant="outlined">
                                    {translate('btn_cancel')}
                                </Button>
                            </Grid2>
                            <Grid2>
                                <Button type="submit" variant="contained" color="primary" disabled={!expDetailFormChanged}>
                                    {translate('btn_submit')}
                                </Button>
                            </Grid2>
                        </Grid2>
                    </form>
                )}
            />
        );
    };
    const handleClickEditExpDetail = (expDetail) => {
        setExpDetailToEdit(expDetail);
        setOpenExpDetailForm(true);
        setExpDetailFormChanged(false);
    };

    // 4. Función para cerrar el formulario
    const handleCloseExpDetailForm = () => {
        setOpenExpDetailForm(false);
        setExpDetailToEdit(null);
        setExpDetailFormChanged(false);
    };

    const handleClickEditCost = (costId, cost) => {
        setOpenCostForm(true);
        setCostToEdit(cost);
        setCostId(costId);
    };

    const handleClickDeleteCost = (costId) => {
        setCostId(costId);
        setOpenDeleteCostForm(true);
    };

    const handleCloseCostForm = () => {
        setOpenCostForm(false);
        setCostId(null);
        setCostToEdit(null);
    };

    const handleCloseDeleteCost = () => {
        setOpenDeleteCostForm(false);
        setCostId(null);
    };

    const handleSubmitDeleteCost = async () => {
        if (costId) {
            await deleteCost(costId);
        }
        setCostId(null);
        setOpenDeleteCostForm(false);
        await getCosts(experienceIndex);
    };

    return (
        <Layout contentClassName={classes.content}>
            <Title value={'Experience'} />
            {/* PDF Viewer Overlay */}
            {openPDFViewer && (
                <div
                    onClick={() => setOpenPDFViewer(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: 'fit-content',
                        padding: '10px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1300,
                    }}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '80%',
                            height: '80%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                        }}>
                        <PDFViewer fileUrl={`${process.env.REACT_APP_CDN_URL}${selectedPDF?.TermsURL}`} fileName={selectedPDF?.TermsName} pageNumber={pageNumber} setPageNumber={setPageNumber} />
                    </div>
                </div>
            )}
            <div>
                {loading ? (
                    <CircularIndeterminate />
                ) : (
                    <>
                        {/* STARTS EXPERIENCER INFO */}
                        <div className={classes.formContainer}>
                            <Title value={experience.name} size={'l'} />
                            <Form
                                onSubmit={onSubmit}
                                initialValues={{
                                    ...experienceData,
                                    insuranceProviderId: experienceData.insuranceProviderId ?? 0,
                                }}
                                render={({ handleSubmit, form }) => (
                                    <form id="form" onSubmit={handleSubmit}>
                                        <div>
                                            <div className={classes.divEditButton}>
                                                <Typography
                                                    style={{
                                                        fontWeight: 'bold',
                                                        marginBottom: '10px',
                                                    }}
                                                    variant="h5"
                                                    className={classes.text}>
                                                    {translate('experience.edit_experience_data')}
                                                </Typography>
                                                <Button
                                                    type="button"
                                                    onClick={() => {
                                                        setCanEdit(true);
                                                    }}
                                                    style={{
                                                        height: 40,
                                                        margin: 'inherit',
                                                    }}
                                                    className={classes.editButton}>
                                                    <img src={EditIcon} alt="edit-icon" width={20} />
                                                    {isSmall ? null : <>&nbsp;</>}
                                                    {isSmall ? null : `${translate('experience.edit')}`}
                                                </Button>
                                            </div>

                                            <Divider />

                                            <div
                                                style={{
                                                    marginTop: '20px',
                                                    marginBottom: '20px',
                                                }}>
                                                <Grid2 container spacing={3}>
                                                    <Grid2 size={{ xs: 12, sm: 3 }}>
                                                        {experience.pictureURL ? (
                                                            <>
                                                                <Card
                                                                    style={{
                                                                        // minWidth: 275,
                                                                        maxWidth: 375,
                                                                    }}>
                                                                    <CardContent>
                                                                        <img
                                                                            src={`${process.env.REACT_APP_CDN_URL}${experience.pictureURL}`}
                                                                            alt="experience-image"
                                                                            style={{
                                                                                width: '100%',
                                                                            }}
                                                                        />
                                                                    </CardContent>
                                                                </Card>
                                                                <br />
                                                            </>
                                                        ) : null}
                                                    </Grid2>
                                                    <Grid2 size={{ xs: 12, sm: 9 }}>
                                                        <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3} mb={1}>
                                                            <Grid2 size={{ xs: 12, sm: 6 }} container spacing={1}>
                                                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                    <Field id="id" name="id" component={TextInput} hintText={translate('experience.id')} label={translate('experience.id')} disabled />
                                                                </Grid2>
                                                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                    <Field name="experiencerId" id="experiencerId" component={TextInput} hintText={translate('experience.experiencerId')} label={translate('experience.experiencerId')} disabled={true} />
                                                                </Grid2>
                                                            </Grid2>
                                                            <Grid2 size={{ xs: 12, sm: 6 }} container spacing={1}>
                                                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                    <Field
                                                                        name="gygActivityReference"
                                                                        id="gygActivityReference"
                                                                        component={TextInput}
                                                                        onFieldChange={(value) => {
                                                                            experienceInfo.gygActivityReference = value;
                                                                            setExperienceInfo(experienceInfo);
                                                                            setExperienceChanged(true);
                                                                        }}
                                                                        hintText={'GYG Activity Reference'}
                                                                        label={'GYG Activity Reference'}
                                                                        disabled={!canEdit}
                                                                    />
                                                                </Grid2>
                                                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                    <Field
                                                                        name="octoReference"
                                                                        id="octoReference"
                                                                        component={TextInput}
                                                                        onFieldChange={(value) => {
                                                                            experienceInfo.octoReference = value;
                                                                            setExperienceInfo(experienceInfo);
                                                                            setExperienceChanged(true);
                                                                        }}
                                                                        hintText={'Octo Reference'}
                                                                        label={'Octo Reference'}
                                                                        disabled={!canEdit}
                                                                    />
                                                                </Grid2>
                                                            </Grid2>
                                                        </Grid2>
                                                        <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3} mb={1}>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    onFieldChange={(value) => {
                                                                        experienceInfo.name = value;
                                                                        setExperienceInfo(experienceInfo);
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    id="name"
                                                                    name="name"
                                                                    component={TextInput}
                                                                    hintText={translate('experience.name')}
                                                                    label={translate('experience.name')}
                                                                    disabled={!canEdit}
                                                                />
                                                            </Grid2>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    onFieldChange={(value) => {
                                                                        experienceInfo.description1 = value;
                                                                        setExperienceInfo(experienceInfo);
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    name="description1"
                                                                    id="description1"
                                                                    component={TextInput}
                                                                    hintText={translate('experience.description1')}
                                                                    label={translate('experience.description1')}
                                                                    disabled={!canEdit}
                                                                />
                                                            </Grid2>
                                                        </Grid2>
                                                        <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3} mb={1}>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    onFieldChange={(value) => {
                                                                        experienceInfo.anticipation = value;
                                                                        setExperienceInfo(experienceInfo);
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    id="anticipation"
                                                                    name="anticipation"
                                                                    component={TextInput}
                                                                    hintText={translate('experience.anticipation')}
                                                                    label={translate('experience.anticipation')}
                                                                    disabled={!canEdit}
                                                                />
                                                            </Grid2>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    name="viatorReference"
                                                                    id="viatorReference"
                                                                    component={TextInput}
                                                                    onFieldChange={(value) => {
                                                                        experienceInfo.viatorReference = value;
                                                                        setExperienceInfo(experienceInfo);
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    hintText={'Viator Reference'}
                                                                    label={'Viator Reference'}
                                                                    disabled={!canEdit}
                                                                />
                                                            </Grid2>
                                                        </Grid2>
                                                        <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3} mb={1}>
                                                            <Grid2 size={{ xs: 12, sm: 12 }}>
                                                                <Field
                                                                    id="price"
                                                                    name="price"
                                                                    label={translate('experience.price')}
                                                                    currency={experienceInfo.currency}
                                                                    disabled={!canEdit}
                                                                    onCurrencyChange={(value) => {
                                                                        experienceInfo.currency = value;
                                                                        setExperienceInfo(experienceInfo);
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    render={({ input, meta, ...rest }) => (
                                                                        <CurrencyInput
                                                                            {...rest}
                                                                            value={typeof input.value === 'number' ? input.value : 0}
                                                                            onChange={(value) => {
                                                                                input.onChange(value);
                                                                                experienceInfo.price = value;
                                                                                setExperienceInfo(experienceInfo);
                                                                                setExperienceChanged(true);
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </Grid2>
                                                        </Grid2>
                                                        <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3} mb={1}>
                                                            <Grid2 size={{ xs: 12, sm: 6 }} container spacing={1}>
                                                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                    <Field
                                                                        id="payNowType"
                                                                        name="payNowType"
                                                                        component={SelectComponent}
                                                                        values={payNowOptions}
                                                                        label="Tipo de Anticipo"
                                                                        labelId="payNowType-label"
                                                                        style={{ width: '100%' }}
                                                                        onFieldChange={(event) => {
                                                                            const value = event.target.value;
                                                                            experienceInfo.payNowType = value;
                                                                            setExperienceInfo({ ...experienceInfo });
                                                                            setExperienceChanged(true);
                                                                        }}
                                                                        disabled={!canEdit}
                                                                    />
                                                                </Grid2>
                                                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                    <Field
                                                                        onFieldChange={(value) => {
                                                                            experienceInfo.payNow = value;
                                                                            setExperienceInfo(experienceInfo);
                                                                            setExperienceChanged(true);
                                                                        }}
                                                                        id="payNow"
                                                                        name="payNow"
                                                                        sx={{ width: '100%' }}
                                                                        component={TextInput}
                                                                        hintText={translate('experience.payNow')}
                                                                        label={translate('experience.payNow')}
                                                                        disabled={!canEdit}
                                                                    />
                                                                </Grid2>
                                                            </Grid2>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    id="priceGroupId"
                                                                    name="priceGroupId"
                                                                    component={SelectComponent}
                                                                    values={[{ id: null, name: '' }, ...priceGroup]}
                                                                    label="Price Group"
                                                                    labelId="priceGroupId"
                                                                    style={{ width: '100%' }}
                                                                    disabled={!canEdit}
                                                                    onFieldChange={(event) => {
                                                                        const value = event.target.value;
                                                                        experienceInfo.priceGroupId = value;
                                                                        setExperienceInfo({ ...experienceInfo });
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                />
                                                            </Grid2>
                                                        </Grid2>
                                                        <Grid2 container direction={'row'} justifyContent={'flex-end'} alignItems={'center'} spacing={3} mb={1}>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    onFieldChange={(value) => {
                                                                        experienceInfo.duration = value;
                                                                        setExperienceInfo(experienceInfo);
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    id="duration"
                                                                    name="duration"
                                                                    component={TextInput}
                                                                    hintText={translate('experience.duration')}
                                                                    label={translate('experience.duration')}
                                                                    disabled={!canEdit}
                                                                />
                                                            </Grid2>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    onFieldChange={(value) => {
                                                                        experienceInfo.location = value;
                                                                        setExperienceInfo(experienceInfo);
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    id="location"
                                                                    name="location"
                                                                    component={TextInput}
                                                                    hintText={translate('experience.location')}
                                                                    label={translate('experience.location')}
                                                                    disabled={!canEdit}
                                                                />
                                                            </Grid2>
                                                        </Grid2>
                                                        <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3} mb={1}>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    style={{
                                                                        paddingTop: '0',
                                                                    }}
                                                                    disabled={!canEdit}
                                                                    checked={requireId}
                                                                    onChange={(value) => {
                                                                        setRequireId(value);
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    name="requireId"
                                                                    component={CheckboxComponent}
                                                                    labelComponent={translate('experience.requireId')}
                                                                />
                                                            </Grid2>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field id="createdOn" name="createdOn" component={TextInput} hintText={translate('experience.createdOn')} label={translate('experience.createdOn')} disabled />
                                                            </Grid2>
                                                        </Grid2>
                                                        <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3} mb={1}>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    id="status"
                                                                    name="status"
                                                                    component={SelectComponent}
                                                                    values={statusOptions}
                                                                    label="Estado"
                                                                    labelId="status-label"
                                                                    style={{ width: '100%' }}
                                                                    onFieldChange={(event) => {
                                                                        const value = event.target.value;
                                                                        experienceInfo.status = value;
                                                                        setExperienceInfo({ ...experienceInfo });
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    disabled={!canEdit}
                                                                />
                                                            </Grid2>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field id="publishedOn" name="publishedOn" component={TextInput} hintText={translate('experience.publishedOn')} label={translate('experience.publishedOn')} disabled />
                                                            </Grid2>
                                                        </Grid2>
                                                        <Grid2 container direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={3} mb={1}>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    id="insuranceProviderId"
                                                                    name="insuranceProviderId"
                                                                    component={SelectComponent}
                                                                    values={insuranceProvider}
                                                                    label={translate('experience.insuranceProvider')}
                                                                    labelId="insurance-provider"
                                                                    style={{ width: '100%' }}
                                                                    onFieldChange={(event) => {
                                                                        const value = event.target.value;
                                                                        experienceInfo.insuranceProviderId = value;
                                                                        if (value !== 0) {
                                                                            getInsurancesProducts(experienceInfo.insuranceProviderId);
                                                                        } else {
                                                                            form.change('insuranceProductId', null);
                                                                        }
                                                                        setExperienceInfo({ ...experienceInfo });
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    disabled={!canEdit || !requireId}
                                                                />
                                                            </Grid2>
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Field
                                                                    id="insuranceProductId"
                                                                    name="insuranceProductId"
                                                                    component={SelectComponent}
                                                                    values={insuranceProduct}
                                                                    label={translate('experience.insuranceProduct')}
                                                                    labelId="insurance-product"
                                                                    style={{ width: '100%' }}
                                                                    onFieldChange={(event) => {
                                                                        const value = event.target.value;
                                                                        experienceInfo.insuranceProductId = value;
                                                                        setExperienceInfo({ ...experienceInfo });
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    disabled={!canEdit || !experienceInfo.insuranceProviderId || !requireId}
                                                                />
                                                            </Grid2>
                                                        </Grid2>
                                                        <Grid2 container justifyContent={'center'} alignItems={'center'}>
                                                            <Grid2 size={12}>
                                                                <Field
                                                                    style={{
                                                                        // marginBottom:
                                                                        //     '2em',
                                                                        width: '99%',
                                                                    }}
                                                                    onFieldChange={(value) => {
                                                                        experienceInfo.description2 = value;
                                                                        setExperienceInfo(experienceInfo);
                                                                        setExperienceChanged(true);
                                                                    }}
                                                                    id="description2"
                                                                    name="description2"
                                                                    component={'textarea'}
                                                                    label={translate('experience.description2')}
                                                                    disabled={!canEdit}
                                                                    multiline
                                                                    rows={8}
                                                                    // initialCharacters={contract?.description?.length || 500}
                                                                />
                                                            </Grid2>
                                                        </Grid2>
                                                    </Grid2>
                                                </Grid2>
                                            </div>
                                        </div>

                                        <Grid2 container justifyContent="flex-end" alignItems="flex-end">
                                            <Grid2 container justify={'space-between'} style={{ width: 'auto' }}>
                                                <Button
                                                    className="rounded mr-3 height-40 w-100px"
                                                    disabled={!canEdit}
                                                    type="button"
                                                    onClick={(e) => {
                                                        setCanEdit(false);
                                                        return form.reset(e);
                                                    }}>
                                                    {translate('btn_cancel')}
                                                </Button>
                                                <Button
                                                    onClick={(e) => {
                                                        setCanEdit(false);
                                                        return handleSubmit(e);
                                                    }}
                                                    className="rounded height-40 w-100px triper-button bg-green-300"
                                                    disabled={!canEdit}
                                                    variant="contained"
                                                    type="submit">
                                                    {translate('btn_submit')}
                                                </Button>
                                            </Grid2>
                                        </Grid2>
                                    </form>
                                )}
                            />
                        </div>

                        {experience?.id && (
                            <Grid2 container width="100%" justifyContent="space-between" alignItems="flex-start" my={4} direction={{ xs: 'column', lg: 'row' }}>
                                {/* Commissions Table */}
                                <Grid2 container size={{ xs: 12, lg: 5.5 }} flexDirection="column">
                                    <Grid2 width="100%" className={classes.tableHeader}>
                                        <Title size="s" value="Experience Terms" />
                                        <Grid2>
                                            <Button variant="contained" onClick={handleOpenUploadPDF}>
                                                {translate('btn_add')}
                                            </Button>
                                        </Grid2>
                                    </Grid2>

                                    <Grid2>
                                        <BasicTable columns={expTermsColumns} values={prettifyExperienceTerms(experiencesTerms)} />
                                    </Grid2>
                                </Grid2>

                                {/* Costs Table */}
                                <Grid2 container size={{ xs: 12, lg: 5.5 }} flexDirection="column">
                                    <Grid2 width="100%" className={classes.tableHeader}>
                                        <Title size="s" value={translate('experiencer.costs.title')} />
                                        <Grid2>
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    setOpenCostForm(true);
                                                }}>
                                                {translate('btn_add')}
                                            </Button>
                                        </Grid2>
                                    </Grid2>
                                    <Grid2>
                                        <BasicTable columns={costColumns(false)} values={prettifyCosts(costs, classes, handleClickEditCost, handleClickDeleteCost)} />
                                    </Grid2>
                                </Grid2>

                                <Grid2 container size={{ xs: 12, lg: 12 }} flexDirection="column">
                                    <Grid2 width="100%" className={classes.tableHeader}>
                                        <Title size="s" value={translate('experience.expDetails')} />
                                    </Grid2>
                                    <Grid2>
                                        <BasicTable columns={expDetailColumns} values={prettifyExperienceDetails(experienceDetails)} />
                                    </Grid2>
                                </Grid2>
                            </Grid2>
                        )}

                        {/* ENDS EXPERIENCER INFO */}

                        <UploadPDFModal open={openUploadPDF} onClose={handleCloseUploadPDF} onSubmit={onSubmitPDF} />

                        {/* Cost Form */}
                        {experience?.id && (
                            <>
                                <AlertDialog
                                    open={openCostForm}
                                    dialogTitle={costId && costToEdit ? translate('edit_cost.title.edit') : translate('edit_cost.title.new')}
                                    dialogContent={
                                        <CostForm
                                            id={costId}
                                            initialCost={costToEdit}
                                            entity="experience"
                                            showOrigin={false}
                                            entityId={experience?.id}
                                            loading={loadingSubmit}
                                            fetchMethod={costId && costToEdit ? Method.PATCH : Method.POST}
                                            onClose={(isSubmit = false) => {
                                                handleCloseCostForm();
                                                if (isSubmit) {
                                                    getCosts(experienceIndex);
                                                }
                                            }}
                                            postCost={postCost}
                                            updateCost={patchCost}
                                        />
                                    }
                                    handleClose={handleCloseCostForm}
                                    hideActions
                                    maxWidth="sm"
                                    fullWidth
                                />

                                <AlertDialog open={openDeleteCostForm} dialogTitle={translate('edit_cost.delete.title')} dialogContent={`${translate('edit_cost.delete.content')} ${costId}?`} handleClose={handleCloseDeleteCost} handleSubmit={handleSubmitDeleteCost} />
                            </>
                        )}

                        {/* Exp Detail Form */}
                        {experience?.id && <AlertDialog open={openExpDetailForm} dialogTitle={translate('experience.edit_experience_detail_data')} dialogContent={renderExpDetailForm()} handleClose={handleCloseExpDetailForm} hideActions maxWidth="sm" fullWidth />}
                        <Divider />
                        <CalendarPage />
                    </>
                )}
            </div>
        </Layout>
    );
};

ExperiencePage.displayName = 'ExperiencePage';

export default ExperiencePage;
